import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LockClosedIcon } from "@heroicons/react/24/outline";

function formatLockedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function CustomerLockBanner({ customer, compact = false, className = "" }) {
  const { t } = useTranslation();
  if (!customer?.profile_locked) return null;

  const who =
    customer.locked_by?.displayTitle ||
    customer.locked_by?.name ||
    t("customer.lock.unknown_user");
  const when = formatLockedAt(customer.locked_at);
  const reason =
    customer.lock_reason || t("customer.lock.no_reason");

  return (
    <div
      className={`border-l-4 border-red-500 bg-red-50 px-4 py-3 ${className}`}
      role="status"
    >
      <div className="flex items-start gap-3">
        <LockClosedIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-red-800">
            {t("customer.lock.banner_title", {
              name: customer.name || customer.displayTitle || "",
            })}
          </p>
          <p className="mt-1 text-sm text-red-700">
            {t("customer.lock.banner_reason", { reason })}
          </p>
          {!compact && (
            <p className="mt-1 text-xs text-red-600">
              {t("customer.lock.banner_meta", { who, when: when || "—" })}
            </p>
          )}
          {compact && customer.id && (
            <Link
              to={`/dashboard/customers/${customer.id}`}
              className="mt-1 inline-block text-xs font-medium text-red-800 underline"
            >
              {t("customer.lock.open_profile")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export function CustomerLockFloatingNotice({ customers = [] }) {
  const { t } = useTranslation();
  const locked = (customers || []).filter((c) => c?.profile_locked && c?.id);
  if (!locked.length) return null;

  return (
    <div className="pointer-events-none fixed left-1/2 top-20 z-40 flex w-[min(20rem,calc(100vw-1.5rem))] -translate-x-1/2 flex-col gap-2">
      {locked.map((customer) => {
        const reason = customer.lock_reason || t("customer.lock.no_reason");
        return (
          <Link
            key={customer.id}
            to={`/dashboard/customers/${customer.id}`}
            className="pointer-events-auto flex items-start gap-2 rounded-lg border border-red-200 bg-white/95 px-3 py-2 text-left shadow-lg shadow-red-900/10 backdrop-blur"
            title={reason}
          >
            <LockClosedIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
            <span className="min-w-0">
              <span className="block truncate text-xs font-semibold text-red-800">
                {t("customer.lock.badge")}:{" "}
                {customer.name || customer.displayTitle || customer.id}
              </span>
              <span className="mt-0.5 block truncate text-[11px] text-red-600">
                {reason}
              </span>
            </span>
          </Link>
        );
      })}
    </div>
  );
}

export function CustomerLockBadge({ customer, className = "" }) {
  const { t } = useTranslation();
  if (!customer?.profile_locked) return null;
  return (
    <span
      className={`inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 ${className}`}
    >
      {t("customer.lock.badge")}
    </span>
  );
}
